import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

/**
 * EodRecipientChips — controlled add/remove chip list with per-address
 * enable toggles, used by the End of Day Auto-Email section in Settings.
 *
 * Props:
 *   value:    Array<{email: string, enabled: boolean}>
 *   onChange: (next) => void
 *
 * Behavior:
 *   - Enter in the input, or clicking Add, appends a new enabled chip.
 *   - Duplicate emails are silently deduped (case-insensitive) so managers
 *     can't accidentally send the same close-out twice.
 *   - Basic email shape validation before adding — nothing fancy, just a
 *     dot after an @, which catches ~95% of typos without a full RFC parser.
 *   - Toggling a chip's Switch flips ``enabled`` in place; the send route
 *     honors this on both the manual "Send Now" and the nightly scheduler.
 *   - The × button removes a chip entirely.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EodRecipientChips = ({ value = [], onChange }) => {
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState("");

  const add = () => {
    const email = draft.trim();
    if (!email) return;
    if (!EMAIL_RE.test(email)) {
      setErr("That doesn't look like an email address");
      return;
    }
    const lower = email.toLowerCase();
    if (value.some((r) => (r.email || "").toLowerCase() === lower)) {
      setErr("Already on the list");
      return;
    }
    onChange([...value, { email, enabled: true }]);
    setDraft("");
    setErr("");
  };

  const toggle = (idx) => {
    onChange(
      value.map((r, i) => (i === idx ? { ...r, enabled: !r.enabled } : r))
    );
  };

  const remove = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const activeCount = value.filter((r) => r.enabled !== false).length;

  return (
    <div className="space-y-2" data-testid="eod-recipient-chips">
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setErr(""); }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="manager@yourdomain.com"
          type="email"
          data-testid="eod-recipients-input"
        />
        <Button
          type="button"
          onClick={add}
          disabled={!draft.trim()}
          data-testid="eod-recipient-add"
        >
          Add
        </Button>
      </div>
      {err && (
        <div className="text-xs text-destructive" data-testid="eod-recipient-error">
          {err}
        </div>
      )}
      {value.length === 0 ? (
        <div className="text-xs text-muted-foreground">
          No recipients yet. Add at least one email and enable it to start
          receiving nightly reports.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {value.map((r, idx) => (
            <div
              key={`${r.email}-${idx}`}
              className={`group inline-flex items-center gap-2 rounded-full border pl-3 pr-1.5 py-1 text-sm transition-colors ${
                r.enabled === false
                  ? "bg-secondary/20 text-muted-foreground line-through decoration-1"
                  : "bg-secondary/50"
              }`}
              data-testid={`eod-chip-${idx}`}
            >
              <span className="max-w-[220px] truncate" title={r.email}>{r.email}</span>
              <Switch
                checked={r.enabled !== false}
                onCheckedChange={() => toggle(idx)}
                className="scale-75"
                data-testid={`eod-chip-toggle-${idx}`}
              />
              <button
                type="button"
                onClick={() => remove(idx)}
                className="ml-0.5 text-muted-foreground hover:text-destructive w-5 h-5 rounded-full inline-flex items-center justify-center"
                aria-label={`Remove ${r.email}`}
                data-testid={`eod-chip-remove-${idx}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="text-[10px] text-muted-foreground" data-testid="eod-recipient-summary">
        {activeCount} of {value.length} recipient{value.length === 1 ? "" : "s"} active
      </div>
    </div>
  );
};

export default EodRecipientChips;
